import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Slider from 'react-slick';

import { Loader, Image } from '@components/common';
import { JobService } from '@services';
import { DAYS_OF_WEEK } from '@constants';
import { RatingModal } from '../../components';
import NewJobModal from './components/NewJobModal';

import '../../styles/slick/slick.scss';
import '../../styles/slick/slick-theme.scss';
import './Overview.scss';

class Overview extends Component {
	constructor(props) {
		super(props);
		this.state = {
			jobs             : [],
			selectedJobIndex : 0,
			currentJob       : {},
			selectedPosIndex : 0,
			upcomingJobText  : '',
			jobsLoaded       : false,
			showPositions    : true,
			showNewJobModal  : false,
		};

		this.jobSlider = createRef();
	}

	componentDidMount() {
		const reviewRequest = localStorage.getItem('review');
		if (reviewRequest) {
			const reviewData = JSON.parse(reviewRequest);
			this.openOfficerRatingModal(reviewData);
			localStorage.removeItem('review');
		}

		const dateSettings = { month : '2-digit', day : '2-digit', year : 'numeric' };
		// Dates should range between 30 days before and after the current date
		const startDate = new Date(new Date().getTime() - (1000 * 60 * 60 * 24 * 30));
		const endDate = new Date(new Date().getTime() + (1000 * 60 * 60 * 24 * 30));

		let jobs = [];
		// First, get all completed jobs within the given date range
		JobService.getAll({
			asc       : true,
			unStaffed : false,
			status    : 3,
			startDate : startDate.toLocaleString('en-ca', dateSettings),
			endDate   : new Date().toLocaleString('en-ca', dateSettings),
		})
			.then(({ data : { ItemList : upcomingJobs } }) => {
				jobs = [...jobs, ...upcomingJobs];
				// Next, get all active jobs
				JobService.getAll({
					asc       : true,
					unStaffed : false,
					status    : 1,
				})
					.then(({ data : { ItemList : activeJobs } }) => {
						jobs = [...jobs, ...activeJobs];
						// Finaly, get all upcoming jobs within the given date range
						JobService.getAll({
							asc       : true,
							unStaffed : false,
							status    : 2,
							startDate : new Date().toLocaleString('en-ca', dateSettings),
							endDate   : endDate.toLocaleString('en-ca', dateSettings),
						})
							.then(({ data : { ItemList : completedJobs } }) => {
								jobs = [...jobs, ...completedJobs];
								const selectedJobIndex = this.getStartingJobIndex(jobs);
								const upcomingJobText = this.getUpcomingJobText(jobs, selectedJobIndex);
								this.setState({
									jobs,
									selectedJobIndex,
									upcomingJobText,
									currentJob : jobs.length > 0 && selectedJobIndex >= 0 ? jobs[selectedJobIndex] : {},
									jobsLoaded : true,
								});
							});
					});
			})
			.catch(() => {
				const selectedJobIndex = this.getStartingJobIndex(jobs);
				const upcomingJobText = this.getUpcomingJobText(jobs, selectedJobIndex);
				this.setState({
					jobs,
					selectedJobIndex,
					upcomingJobText,
					currentJob : jobs.length > 0 && selectedJobIndex >= 0 ? jobs[selectedJobIndex] : {},
					jobsLoaded : true,
				});
			});
	}

	componentDidUpdate(prevProps, prevState) {
		const { selectedJobIndex : prevSelectedJobIndex } = prevState;
		const { selectedJobIndex } = this.state;
		if (prevSelectedJobIndex !== selectedJobIndex) {
			// eslint-disable-next-line
			this.setState({ selectedPosIndex : 0 });
		}
	}

	openOfficerRatingModal = (data) => {
		this._officerRatingModal.openModal(data);
	}

	openNewJobModal = () => {
		this.setState({ showNewJobModal : true });
	}

	closeNewJobModal = () => {
		this.setState({ showNewJobModal : false });
	}

	getStartingJobIndex = (jobs = []) => {
		if (jobs.length === 0) return 0;
		let index = jobs.findIndex(({ StartDateTime, EndDateTime }) => {
			const dateFormat = { month : 'numeric', day : 'numeric', year : 'numeric' };
			const jobStartDate = new Date(`${StartDateTime}Z`);
			const jobEndDate = new Date(`${EndDateTime}Z`);
			const today = new Date();
			// If a job is scheduled to start today, check if it's already been completed
			if (jobStartDate.toLocaleString(undefined, dateFormat) === today.toLocaleString(undefined, dateFormat)) {
				// if the job has not yet ended, this is the index we want
				return jobEndDate.getTime() > today.getTime();
			}
			// If there aren't jobs that match today's date, get the next upcoming job
			return jobStartDate.getTime() >= today.getTime();
		});
		// If no upcoming jobs are found, set the index to the last element of the jobs array
		if (index < 0) index = jobs.length - 1;

		return index;
	}

	getUpcomingJobText = (jobs, upcomingIndex) => {
		if (jobs.length === 0) return '';
		if (jobs[upcomingIndex]) {
			const dateFormat = { month : 'numeric', day : 'numeric', year : 'numeric' };
			const { StartDateTime } = jobs[upcomingIndex];
			const jobStartDate = new Date(`${StartDateTime}Z`);
			const today = new Date();
			const tomorrow = new Date();
			tomorrow.setDate(tomorrow.getDate() + 1);

			if (!Number.isNaN(jobStartDate.getTime())) {
				if (jobStartDate.toLocaleString(undefined, dateFormat) === today.toLocaleString(undefined, dateFormat)) {
					return 'You have a RollKall job today!';
				}
				if (jobStartDate.toLocaleString(undefined, dateFormat) === tomorrow.toLocaleString(undefined, dateFormat)) {
					return 'You have a RollKall job coming up tomorrow';
				}
				const weekFromToday = new Date();
				weekFromToday.setDate(weekFromToday.getDate() + 7);
				if (jobStartDate.getTime() > today.getTime() && jobStartDate.getTime() < weekFromToday.getTime()) {
					const dayOfWeek = DAYS_OF_WEEK.find(({ value }) => jobStartDate.getDay() + 1 === value).label;
					return `You have a RollKall job coming up on ${dayOfWeek}`;
				}
			}
		}

		return 'You have no jobs coming up within the next week';
	}

	getGreeting = () => {
		const { user : { FirstName } } = this.props;
		const hours = new Date().getHours();
		if (hours >= 6 && hours < 12) return `Good morning, ${FirstName}`;
		if (hours >= 12 && hours < 17) return `Good afternoon, ${FirstName}`;
		return `Good evening, ${FirstName}`;
	}

	changeCurrentJob = (index) => {
		const { jobs } = this.state;
		this.setState({
			selectedJobIndex : index,
			currentJob       : jobs[index],
		}, () => {
			// slideOffset is used to center the selected slide
			// breakpointDiff ensures that we dont slide past the last job to show empty space
			let slideOffset = 3;
			let breakpointDiff = 6;
			if (window.innerWidth < 576) {
				slideOffset = 0;
				breakpointDiff = 0;
			} else if (window.innerWidth < 680) {
				slideOffset = 1;
				breakpointDiff = 2;
			} else if (window.innerWidth < 992) {
				slideOffset = 2;
				breakpointDiff = 4;
			}

			if (index + slideOffset >= jobs.length) {
				this.jobSlider.slickGoTo(jobs.length - 1 - breakpointDiff);
			} else this.jobSlider.slickGoTo(Math.max(index - slideOffset, 0));
		});
	}

	handleChange = (index) => {
		// On mobile views, we will only show one job at a time.
		// If an arrow is clicked, we should change the current job to reflect what's visible
		if (window.innerWidth < 576) {
			const { jobs } = this.state;
			this.setState({
				selectedJobIndex : index,
				currentJob       : jobs[index],
			});
		}
	};

	jobMonthDay = (dateString) => {
		// job timestamps from API are in UTC but do not have a Z at the end
		const upcomingDate = new Date(`${dateString}Z`);
		if (!Number.isNaN(upcomingDate.getTime())) {
			const month = upcomingDate.toLocaleString(undefined, { month : 'short' });
			const day = upcomingDate.getDate();

			return { month, day };
		}

		return { month : '--', day : '--' };
	}

	myJobClass = (startDate, index) => {
		const { selectedJobIndex } = this.state;
		let className = 'my-job';
		if (index === selectedJobIndex) className = `${className} current`;
		if (new Date(`${startDate}Z`).getTime() < new Date().getTime()) className = `${className} previous`;
		return className;
	}

	SliderArrow = ({ direction }) => {
		const { selectedJobIndex, jobs } = this.state;
		let disabled = false;
		if (window.innerWidth < 576 && (
			(direction === 'left' && selectedJobIndex === 0) || (direction === 'right' && selectedJobIndex === jobs.length - 1)
		)) {
			disabled = true;
		}

		return (
			<div>
				{/* eslint-disable-next-line */}
				<button
					type="button"
					id={`slider-arrow-${direction}`}
					className={`btn btn-link rkf rkf-chevron-${direction}${disabled ? ' disabled' : ''}`}
				/>
			</div>
		);
	}

	JobStartEndInfo = ({ StartDateTime, EndDateTime }) => {
		const start = new Date(`${StartDateTime}Z`);
		const end = new Date(`${EndDateTime}Z`);
		const startDate = start.toLocaleString(undefined, { month : 'numeric', day : 'numeric', year : 'numeric' });
		const startTime = start.toLocaleString(undefined, { hour : 'numeric', minute : '2-digit', hour12 : true });
		const endTime = end.toLocaleString(undefined, { hour : 'numeric', minute : '2-digit', hour12 : true });

		return (
			<div className="job-detail-section">
				<div><strong>{startDate}</strong></div>
				<div><strong>{`${startTime} - ${endTime}`}</strong></div>
			</div>
		);
	}

	PositionDetails = (currentPosition) => {
		const {
			AssignedOfficers,
			ExternalOfficers,
			AssignedOfficersCount,
			...position
		} = currentPosition;
		let OfficerProfile;
		if (AssignedOfficersCount > 0 && AssignedOfficers.length > 0) ([{ OfficerProfile }] = AssignedOfficers);
		else if (AssignedOfficersCount > 0 && ExternalOfficers.length > 0) ([OfficerProfile] = ExternalOfficers);
		return (
			<div className="assigned-officer-details">
				<Choose>
					<When condition={AssignedOfficersCount === 0}>
						<h3>Awaiting assignment</h3>
					</When>
					<When condition={AssignedOfficersCount === 1 && AssignedOfficers.length === 1}>
						<h3>{`${OfficerProfile.FirstName} ${OfficerProfile.LastName}`}</h3>
						<div>
							{`${OfficerProfile.OfficerRank}, ${OfficerProfile.JurisdictionName}`}
						</div>
					</When>
					<When condition={AssignedOfficersCount === 1 && ExternalOfficers.length === 1}>
						<h3>{OfficerProfile.OfficerName}</h3>
						<div>
							Non-RollKall Officer
						</div>
					</When>
					<Otherwise>
						<h3>{`${AssignedOfficersCount} officers assigned`}</h3>
					</Otherwise>
				</Choose>
				<If condition={position.DressTypeId === 1 || position.VehicleRequired}>
					<div className="officer-detail-section">
						<strong>Arriving In</strong>
						<If condition={position.DressTypeId === 1}>
							<br />
							{position.DressTypeName}
						</If>
					</div>
					<div className="officer-detail-section">
						<If condition={position.VehicleRequired}>
							{position.VehicleTypes.map((vehicle) => (
								<div key={`vehicle-type-${vehicle.Id}`}>
									{vehicle.Name}
								</div>
							))}
						</If>
					</div>
				</If>
			</div>
		);
	}

	toggleMultiplePositions = () => {
		this.setState((prevState) => ({ showPositions : !prevState.showPositions }));
	}

	changeSelectedPosition = (index) => {
		this.setState({ selectedPosIndex : index });
	}

	getInitialSlide = (nextJob) => {
		const { jobs } = this.state;
		const lastJobIndex = jobs.length - 1;

		// slideOffset is used to center the selected slide
		// breakpointDiff ensures that we dont slide past the last job to show empty space
		let slideOffset = 3;
		let breakpointDiff = 6;
		if (window.innerWidth < 576) {
			slideOffset = 0;
			breakpointDiff = 0;
		} else if (window.innerWidth < 680) {
			slideOffset = 1;
			breakpointDiff = 2;
		} else if (window.innerWidth < 992) {
			slideOffset = 2;
			breakpointDiff = 4;
		}

		if (nextJob >= lastJobIndex - breakpointDiff) {
			return lastJobIndex - breakpointDiff;
		}
		return nextJob - slideOffset;
	};

	formatClockInOut = (timestamp) => {
		const date = new Date(`${timestamp}Z`);
		const format = { hour : 'numeric', minute : '2-digit', hour12 : true };

		return date.toLocaleString(undefined, format);
	}

	render() {
		const {
			jobs,
			currentJob,
			selectedPosIndex,
			upcomingJobText,
			jobsLoaded,
			showPositions,
			showNewJobModal,
		} = this.state;
		const { user : { Organization, TimeZoneId } } = this.props;

		const currentPosition = currentJob.Jobs ? currentJob.Jobs[selectedPosIndex] : {};
		let AssignedOfficer;
		if (currentPosition.AssignedOfficersCount === 1 && currentPosition.AssignedOfficers.length === 1) {
			([AssignedOfficer] = currentPosition.AssignedOfficers);
		} else if (currentPosition.AssignedOfficersCount === 1 && currentPosition.ExternalOfficers.length === 1) {
			// Construct an AssignedOfficer object with all required properties based on info that we have
			AssignedOfficer = {
				isExternal     : true,
				FirstName      : currentPosition.ExternalOfficers[0].OfficerName,
				LastName       : '',
				OfficerProfile : { ContactPhotoUrl : '' },
			};
		}
		let nextJob = jobs.findIndex((job) => new Date(`${job.StartDateTime}Z`).getTime() >= new Date().getTime());
		// To ensure our nextJob isn't -1 in case there are no future jobs
		if (nextJob < 0) nextJob = jobs.length - 1;

		const responsiveSliderSettings = [
			{
				breakpoint : 991,
				settings   : {
					slidesToShow   : 5,
					slidesToScroll : 5,
				},
			},
			{
				breakpoint : 679,
				settings   : {
					slidesToShow   : 3,
					slidesToScroll : 3,
				},
			},
			{
				breakpoint : 575,
				settings   : {
					slidesToShow   : 1,
					slidesToScroll : 1,
					initialSlide   : nextJob,
				},
			},
		];

		return (
			<div id="business-overview" className="container">
				<div className="row">
					<div className="col-xs-7 col-sm-6">
						<h2>{this.getGreeting()}</h2>
					</div>
					<div className="col-xs-5 col-sm-6 text-right">
						<h5 className="org-name">{Organization ? Organization.Name : ''}</h5>
						<button
							type="button"
							className="btn btn-primary"
							id="add-new-job"
							onClick={() => this.openNewJobModal()}
						>
							+ New Job
						</button>
					</div>
				</div>
				<div className="row">
					<h4 className="col-sm-12">{upcomingJobText}</h4>
				</div>
				<div className="row">
					<Choose>
						<When condition={jobs.length === 0 && jobsLoaded}>
							<div className="text-center">
								No jobs found.
								<button
									type="button"
									className="btn btn-link"
									id="create-new-job"
									onClick={() => this.openNewJobModal()}
								>
									Create one today!
								</button>
							</div>
						</When>
						<When condition={jobsLoaded}>
							<div className="col-xs-10 col-xs-offset-1 col-sm-12 col-sm-offset-0" id="my-jobs">
								<h4>My Jobs</h4>
								<Slider
									ref={(slider) => { this.jobSlider = slider; }}
									infinite={false}
									pauseOnHover={false}
									className="my-jobs-slider"
									slidesToShow={7}
									slidesToScroll={7}
									afterChange={this.handleChange}
									prevArrow={this.SliderArrow({ direction : 'left' })}
									nextArrow={this.SliderArrow({ direction : 'right' })}
									initialSlide={this.getInitialSlide(nextJob)}
									responsive={responsiveSliderSettings}
								>
									{jobs.map((job, i) => (
										<button
											key={job.Id}
											type="button"
											onClick={() => this.changeCurrentJob(i)}
											className={this.myJobClass(job.StartDateTime, i)}
										>
											<div>{this.jobMonthDay(job.StartDateTime).month}</div>
											<div className="my-job-day">{this.jobMonthDay(job.StartDateTime).day}</div>
											<div className={`my-job-type event-type-${job.JobEventTypeId}`}>{job.JobEventTypeName}</div>
										</button>
									))}
								</Slider>
							</div>
						</When>
						<Otherwise>
							<div className="text-center">
								<Loader active={!jobsLoaded} spinnerOnly />
							</div>
						</Otherwise>
					</Choose>
				</div>
				<If condition={Object.keys(currentPosition).length > 0 && jobsLoaded}>
					<div className="row">
						<div className="container" id="next-job-details">
							<div className="col-xs-6 col-sm-8">
								<h4 className="job-title">{currentJob.Name || ''}</h4>
								{currentPosition.Title && (
									<h5>
										<strong>Position:&nbsp;</strong>
										{currentPosition.Title}
									</h5>
								)}
							</div>
							<div className="col-xs-6 col-sm-4 text-right">
								<a href="https://support.rollkall.com" target="_blank" rel="noopener noreferrer">
									<button type="button" className="btn btn-default" id="contact-support">
										Have a Question?
									</button>
								</a>
							</div>
							<div className="col-xs-12 container">
								<div className="row">
									<div className="col-xs-6 col-sm-2 col-md-3">
										<div className="officer-image-container text-center">
											<Choose>
												<When condition={currentPosition.AssignedOfficersCount === 0}>
													<Image className="assigned-officer-image" fallbackSrc="avatar" />
												</When>
												<When condition={currentPosition.AssignedOfficersCount === 1}>
													<Image
														className="assigned-officer-image"
														src={AssignedOfficer.OfficerProfile.ContactPhotoUrl}
														alt={`${AssignedOfficer.OfficerProfile.FirstName} ${AssignedOfficer.OfficerProfile.LastName}`}
														fallbackSrc="avatar"
													/>
												</When>
												<Otherwise>
													<Choose>
														<When condition={currentPosition.AssignedOfficersCount > 3}>
															{[...currentPosition.AssignedOfficers, ...currentPosition.ExternalOfficers].slice(0, 3).map(({
																Id,
																OfficerProfile,
																OfficerName,
															}) => (
																<Image
																	key={Id}
																	className="assigned-officer-image multiple"
																	src={OfficerProfile ? OfficerProfile.ContactPhotoUrl : ''}
																	alt={`${OfficerProfile ? OfficerProfile.FirstName : OfficerName} ${OfficerProfile ? OfficerProfile.LastName : ''}`}
																	fallbackSrc="avatar"
																/>
															))}
															<div className="assigned-officers-remaining">
																{`+${currentPosition.AssignedOfficersCount - 3}`}
															</div>
														</When>
														<Otherwise>
															{[...currentPosition.AssignedOfficers, ...currentPosition.ExternalOfficers].map(({ Id, OfficerProfile, OfficerName }) => (
																<Image
																	key={Id}
																	className="assigned-officer-image multiple"
																	src={OfficerProfile ? OfficerProfile.ContactPhotoUrl : ''}
																	alt={`${OfficerProfile ? OfficerProfile.FirstName : OfficerName} ${OfficerProfile ? OfficerProfile.LastName : ''}`}
																	fallbackSrc="avatar"
																/>
															))}
														</Otherwise>
													</Choose>
												</Otherwise>
											</Choose>
										</div>
									</div>
									<div className="col-xs-6 col-sm-3">
										{this.PositionDetails(currentPosition)}
									</div>
									<div className="col-xs-12 col-sm-3">
										<div className="my-job-details">
											{this.JobStartEndInfo(currentPosition)}
											<div className="job-detail-section">
												{currentJob.LocationAddress}
												<br />
												{`${currentJob.LocationCity}, ${currentJob.LocationState} ${currentJob.LocationZipcode}`}
											</div>
											<If condition={currentPosition.Details !== ''}>
												<div className="officer-detail-section">
													<em>{currentPosition.Details}</em>
												</div>
											</If>
											<If condition={AssignedOfficer && AssignedOfficer.ClockIn}>
												<div className="job-detail-section">
													<strong>Clocked In</strong>
													<br />
													<div>{this.formatClockInOut(AssignedOfficer.ClockIn)}</div>
													<If condition={AssignedOfficer.ClockOut}>
														<strong>Clocked Out</strong>
														<br />
														<div>{this.formatClockInOut(AssignedOfficer.ClockOut)}</div>
													</If>
												</div>
											</If>
										</div>
									</div>
									<div className="col-xs-12 col-sm-4 col-md-3 multiple-positions">
										<If condition={currentJob.Jobs.length > 1}>
											<div className="multiple-position-title">
												<h4 className="positions-title-text">{`Multiple Positions (${currentJob.Jobs.length})`}</h4>
												<button
													type="button"
													className="btn btn-link"
													id="toggle-position-collapse"
													onClick={() => this.toggleMultiplePositions()}
												>
													<i className={`rkf rkf-chevron-${showPositions ? 'down' : 'up'}`} />
												</button>
											</div>
											<div className={`multiple-position-container${showPositions ? '' : ' hidden'}`}>
												{currentJob.Jobs.map((position, i) => (
													<button
														type="button"
														className={`btn position-button${selectedPosIndex === i ? ' selected' : ''}`}
														key={position.Id}
														onClick={() => this.changeSelectedPosition(i)}
													>
														<div className="position-title" title={position.Title}>
															<strong>{position.Title}</strong>
														</div>
														<div>
															{`${position.AssignedOfficersCount}/${position.OfficersNeeded} assigned`}
														</div>
													</button>
												))}
											</div>
										</If>
									</div>
								</div>
							</div>
						</div>
					</div>
				</If>
				<RatingModal
					ref={(ref) => { this._officerRatingModal = ref; }}
					timeZoneId={TimeZoneId}
				/>
				<NewJobModal
					show={showNewJobModal}
					onHide={this.closeNewJobModal}
				/>
			</div>
		);
	}
}

const mapStateToProps = ({ app : { user } }) => ({ user });

Overview.propTypes = {
	user : PropTypes.object.isRequired,
};

export default connect(mapStateToProps)(Overview);
